// src/Components/Promo/Portfolio.js

import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

// Navigation Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';

// Page Indicator Icons
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

// Document Generation Imports
import { generatePortfolioPDF } from './PortfolioPDF';
import { generatePortfolioDocx } from './PortfolioDocx';

// Hook
import { usePromoPortfolioManage } from '../../Hooks/usePromoPortfolioManage';

// Sub-components
import PortfolioList from './PortfolioList';
import PortfolioRegions from './PortfolioRegions';
import PortfolioCategories from './PortfolioCategories';
import PortfolioSettings from './PortfolioSettings';

// Components
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import AdminBg from '../../assets/images/AdminBackground.png';

// Icons
import {
  MenuAddNew,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuIconCancel,
  MenuAddNewGrey,
  MenuDeleteGrey,
  MenuDownload,
} from '../../assets/icons/CustomIcons';

// --- HELPER: Force Paint ---
const forcePaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 0);
      });
    });
  });

// --- HELPER: Date Formatter ---
const formatDate = (isoString) => {
  if (!isoString) return '?';
  const parts = isoString.split('-');
  if (parts.length !== 3) return isoString;
  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
};

export default function Portfolio({ setIsDirty }) {
  const { state, flags, alerts, actions } = usePromoPortfolioManage();

  // --- LOCAL STATE ---
  const [viewMode, setViewMode] = useState('LIST');
  const [showClearAlert, setShowClearAlert] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingRowIds, setGeneratingRowIds] = useState([]);

  const [outputFormats, setOutputFormats] = useState({
    generateWord: false,
    generatePdf: false,
  });

  // --- NEW ALERT STATES ---
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // State for the dynamic download success/failure alerts
  const [showDownloadAlert, setShowDownloadAlert] = useState(false);
  const [downloadAlertMessage, setDownloadAlertMessage] = useState('');
  const [downloadAlertSeverity, setDownloadAlertSeverity] = useState('info');

  const activeSteps = ['LIST', 'REGIONS', 'CATEGORIES', 'SETTINGS'];

  const selectedItem =
    state.selectedIds.length === 1
      ? state.displayRows.find((r) => r.id === state.selectedIds[0])
      : null;

  useEffect(() => {
    if (setIsDirty) {
      setIsDirty(flags.isDirty);
    }
  }, [flags.isDirty, setIsDirty]);

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const activeRegions = state.regions.filter((r) => !r.isArchived && !r.archived);
  const activeCategories = state.categories.filter((c) => !c.isArchived && !c.archived);

  // --- HANDLERS ---
  const handleLocalCancel = () => {
    if (flags.isDirty) {
      alerts.setShowUnsaved(true);
    } else {
      actions.resetInterface();
      setViewMode('LIST');
    }
  };

  const handleDeleteClick = () => {
    if (viewMode !== 'LIST') {
      setShowClearAlert(true);
    } else {
      actions.handleDeleteRequest();
    }
  };

  const handleClearConfirm = () => {
    if (selectedItem) {
      actions.handleClearFields(selectedItem.id);
    }
    setShowClearAlert(false);
  };

  const handleNavFirst = () => setViewMode(activeSteps[0]);

  const handleNavBack = () => {
    const currentIndex = activeSteps.indexOf(viewMode);
    if (currentIndex > 0) {
      setViewMode(activeSteps[currentIndex - 1]);
    }
  };

  const handleNavForward = () => {
    const currentIndex = activeSteps.indexOf(viewMode);
    if (currentIndex < activeSteps.length - 1) {
      setViewMode(activeSteps[currentIndex + 1]);
    }
  };

  const handleNavLast = () => setViewMode(activeSteps[activeSteps.length - 1]);

  const handleRegionToggle = (regionId) => {
    if (!selectedItem) return;
    const currentIds = selectedItem.regionIds || [];
    let newIds;
    if (currentIds.includes(regionId)) {
      newIds = currentIds.filter((id) => id !== regionId);
    } else {
      newIds = [...currentIds, regionId];
    }
    actions.handleFieldChange(selectedItem.id, 'regionIds', newIds);
  };

  const handleToggleAllRegions = (checked) => {
    if (!selectedItem) return;
    const newIds = checked ? activeRegions.map((r) => r.id) : [];
    actions.handleFieldChange(selectedItem.id, 'regionIds', newIds);
  };

  const handleCategoryToggle = (catId) => {
    if (!selectedItem) return;
    const currentIds = selectedItem.categoryIds || [];
    let newIds;
    if (currentIds.includes(catId)) {
      newIds = currentIds.filter((id) => id !== catId);
    } else {
      newIds = [...currentIds, catId];
    }
    actions.handleFieldChange(selectedItem.id, 'categoryIds', newIds);
  };

  const handleToggleAllCategories = (checked) => {
    if (!selectedItem) return;
    const newIds = checked ? activeCategories.map((c) => c.id) : [];
    actions.handleFieldChange(selectedItem.id, 'categoryIds', newIds);
  };

  // --- GENERATION LOGIC (Download Local EML/PDF/Html Only) ---
  const handleProceed = async () => {
    if (isGenerating) return;
    if (state.selectedIds.length === 0) return;

    const wantsWord = outputFormats.generateWord;
    const wantsPdf = outputFormats.generatePdf;

    if (!wantsWord && !wantsPdf) {
      setErrorMessage(
        'Please select at least one document format (Word or PDF) in the settings.',
      );
      setShowErrorAlert(true);
      return;
    }

    // --- CHECK FOR DELETED BATCHES ---
    let hasDeletedBatch = false;
    for (let i = 0; i < state.selectedIds.length; i++) {
      const currentId = state.selectedIds[i];
      const currentItem = state.displayRows.find((r) => r.id === currentId);
      if (!currentItem) continue;

      const currentBatches =
        currentItem.Batch ||
        (currentItem.unpublishedBatch ? [currentItem.unpublishedBatch] : []);

      if (Array.isArray(currentBatches)) {
        for (const bId of currentBatches) {
          if (bId !== 'ALL') {
            const found = state.allBatches?.find((b) => b.id === bId);
            if (!found) {
              hasDeletedBatch = true;
              break;
            }
          }
        }
      } else if (typeof currentBatches === 'string' && currentBatches !== 'ALL') {
        const found = state.allBatches?.find((b) => b.id === currentBatches);
        if (!found) {
          hasDeletedBatch = true;
        }
      }
      if (hasDeletedBatch) break;
    }

    if (hasDeletedBatch) {
      setErrorMessage('A batch has been deleted. Check the batch list');
      setShowErrorAlert(true);
      return;
    }

    setShowDownloadAlert(false);

    setGeneratingRowIds(state.selectedIds);

    // Give React a microsecond to unmount the old elements before mounting the new ones
    await new Promise((r) => setTimeout(r, 50));

    setIsGenerating(true);

    try {
      // 1. Fetch Logo & Settings
      let logoUrl = null;
      try {
        const logoDoc = await getDoc(doc(db, 'settings', 'MainLogo'));
        if (logoDoc.exists()) {
          logoUrl = logoDoc.data().url;
        }
      } catch (err) {
        console.error('Error fetching logo:', err);
      }

      let allowRowBreaks = false;
      let useRoundedCorners = false;
      let roundedCornerRadius = 20;
      try {
        const promoDoc = await getDoc(doc(db, 'settings', 'PromoSettings'));
        if (promoDoc.exists()) {
          allowRowBreaks = !!promoDoc.data().allowRowBreaks;
          useRoundedCorners = !!promoDoc.data().useRoundedCorners;
          roundedCornerRadius =
            promoDoc.data().roundedCornerRadius !== undefined
              ? promoDoc.data().roundedCornerRadius
              : 20;
        }
      } catch (err) {
        console.error('Error fetching PromoSettings:', err);
      }

      let emailFromAddress = 'news@updates.loktr.net';
      try {
        const emailFromDoc = await getDoc(doc(db, 'settings', 'EmailFrom'));
        if (emailFromDoc.exists() && emailFromDoc.data().emailFrom) {
          emailFromAddress = emailFromDoc.data().emailFrom;
        }
      } catch (err) {
        console.error('Error fetching EmailFrom:', err);
      }

      let defaultFontType = 'Arial';
      let defaultFontSize = '11pt';
      try {
        const fontDoc = await getDoc(doc(db, 'settings', 'RTEDefaultText'));
        if (fontDoc.exists()) {
          const fd = fontDoc.data();
          if (fd.FontType) defaultFontType = fd.FontType;
          if (fd.FontSize) defaultFontSize = fd.FontSize;
        }
      } catch (err) {
        console.error('Error fetching global font settings:', err);
      }

      // 2. Fetch All Markers
      const querySnapshot = await getDocs(collection(db, 'markerGroups'));
      const allMarkers = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      for (let i = 0; i < state.selectedIds.length; i++) {
        const currentId = state.selectedIds[i];
        const currentItem = state.displayRows.find((r) => r.id === currentId);
        if (!currentItem) continue;

        let batchText = '';
        let batchCommaText = '';
        const currentBatches =
          currentItem.Batch ||
          (currentItem.unpublishedBatch ? [currentItem.unpublishedBatch] : []);

        if (
          Array.isArray(currentBatches) &&
          currentBatches.length > 0 &&
          !currentBatches.includes('ALL')
        ) {
          const sortedBatches = [...currentBatches].sort((aId, bId) => {
            const aFound = state.allBatches?.find((b) => b.id === aId);
            const bFound = state.allBatches?.find((b) => b.id === bId);
            const getMs = (found) => {
              if (!found || !found.importDate) return 0;
              const d = found.importDate;
              if (typeof d.toMillis === 'function') return d.toMillis();
              if (typeof d.toDate === 'function') return d.toDate().getTime();
              if (d.seconds) return d.seconds * 1000;
              return new Date(d).getTime() || 0;
            };
            return getMs(bFound) - getMs(aFound);
          });

          const namesNewline = sortedBatches.map((bId, idx) => {
            const found = state.allBatches?.find((b) => b.id === bId);
            return `${idx + 1}. ${found ? found.filename : `${bId} (Deleted)`}`;
          });
          const namesComma = sortedBatches.map((bId) => {
            const found = state.allBatches?.find((b) => b.id === bId);
            return found ? found.filename : `${bId} (Deleted)`;
          });
          batchText = namesNewline.join('\n');
          batchCommaText = namesComma.join(', ');
        } else if (typeof currentBatches === 'string' && currentBatches !== 'ALL') {
          const found = state.allBatches?.find((b) => b.id === currentBatches);
          batchText = `1. ${found ? found.filename : `${currentBatches} (Deleted)`}`;
          batchCommaText = found ? found.filename : `${currentBatches} (Deleted)`;
        }

        // 3. Filter Markers
        const filteredMarkers = allMarkers.filter((marker) => {
          let vis = marker.visibility;
          if (!vis) {
            if (marker.publicFromDate && marker.publicFromDate.trim() !== '') {
              vis = `PUBLIC FROM ${marker.publicFromDate}`;
            } else if (marker.isPrivate) {
              vis = 'PRIVATE';
            } else {
              vis = 'PUBLIC';
            }
          }

          const upperVis = String(vis).toUpperCase().trim();

          if (upperVis === 'PRIVATE' || upperVis === 'RESTRICTED') return false;

          if (upperVis.startsWith('PUBLIC FROM')) {
            const datePart = upperVis.replace('PUBLIC FROM', '').trim();
            const today = new Date().toISOString().split('T')[0];
            if (datePart && datePart > today) return false;
          }

          if (currentItem.dateRangeType === 'RANGE') {
            if (currentItem.dateFrom && marker.date < currentItem.dateFrom) return false;
            if (currentItem.dateTo && marker.date > currentItem.dateTo) return false;
          }

          if (
            Array.isArray(currentBatches) &&
            currentBatches.length > 0 &&
            !currentBatches.includes('ALL')
          ) {
            if (!currentBatches.includes(marker.importBatchId)) return false;
          } else if (typeof currentBatches === 'string' && currentBatches !== 'ALL') {
            if (marker.importBatchId !== currentBatches) return false;
          }

          if (currentItem.categoryIds && currentItem.categoryIds.length > 0) {
            if (!currentItem.categoryIds.includes(marker.categoryId)) return false;
          } else if (currentItem.categoryId && currentItem.categoryId !== 'ALL') {
            if (marker.categoryId !== currentItem.categoryId) return false;
          }

          if (currentItem.regionIds && currentItem.regionIds.length > 0) {
            if (!currentItem.regionIds.includes(marker.regionId)) return false;
          } else if (currentItem.regionId && currentItem.regionId !== 'ALL') {
            if (marker.regionId !== currentItem.regionId) return false;
          }

          if (currentItem.keywords && currentItem.keywords.trim() !== '') {
            const searchTerms = currentItem.keywords
              .toLowerCase()
              .split(/\s+/)
              .filter((k) => k);
            const markerKeywords = (marker.keywords || '').toLowerCase();
            const match = searchTerms.some((term) => markerKeywords.includes(term));
            if (!match) return false;
          }

          const hasInfoA =
            currentItem.infoFilterA && currentItem.infoFilterA.trim().length > 0;
          const hasInfoB =
            currentItem.infoFilterB && currentItem.infoFilterB.trim().length > 0;

          if (hasInfoA || hasInfoB) {
            const allItemValues = Object.values(marker)
              .filter((val) => val !== null && val !== undefined)
              .map((val) => String(val).toUpperCase())
              .join(' ');

            let passA = true;
            if (hasInfoA) {
              const termsA = currentItem.infoFilterA
                .toUpperCase()
                .split(/\s+/)
                .filter((t) => t);
              passA = termsA.some((term) => allItemValues.includes(term));
            }

            let passB = true;
            if (hasInfoB) {
              const termsB = currentItem.infoFilterB
                .toUpperCase()
                .split(/\s+/)
                .filter((t) => t);
              passB = termsB.some((term) => allItemValues.includes(term));
            }

            if (!passA || !passB) return false;
          }

          return true;
        });

        // --- SORTING: Date Descending, Blanks at Bottom ---
        filteredMarkers.sort((a, b) => {
          const dateA = a.date;
          const dateB = b.date;

          const hasDateA = dateA && dateA.trim() !== '';
          const hasDateB = dateB && dateB.trim() !== '';

          if (hasDateA && hasDateB) {
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            return 0;
          }
          if (hasDateA && !hasDateB) return -1;
          if (!hasDateA && hasDateB) return 1;
          return 0;
        });

        // 4. Limit Results and inject regionName for grouped views
        const limit = parseInt(currentItem.maxResults) || 1000;
        const finalMarkers = filteredMarkers.slice(0, limit).map((marker) => {
          const region = activeRegions.find((r) => r.id === marker.regionId);
          return {
            ...marker,
            regionName: region ? region.name : 'Other',
          };
        });

        if (finalMarkers.length === 0) {
          setDownloadAlertSeverity('warning');
          setDownloadAlertMessage(
            <Box sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: 1.5 }}>
              {`${currentItem.name}:\n${batchText ? batchText + '\n\n' : ''}No locations to download`}
            </Box>,
          );
          setShowDownloadAlert(true);
          await new Promise((r) => setTimeout(r, 2000));
          setShowDownloadAlert(false);
          await new Promise((r) => setTimeout(r, 50));
          continue;
        }

        setDownloadAlertSeverity('info');
        setShowDownloadAlert(true);

        const delayMs = Math.max(30, 2000 / finalMarkers.length);
        for (let locIdx = 0; locIdx < finalMarkers.length; locIdx++) {
          setDownloadAlertMessage(
            <Box sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: 1.5 }}>
              {`Processing ${currentItem.name}:\n${batchText ? batchText + '\n\n' : ''}${locIdx + 1} of ${finalMarkers.length} location(s) being processed`}
            </Box>,
          );
          await forcePaint(); // Yield to paint

          const marker = finalMarkers[locIdx];
          const firstImageMedia = (marker.photos || []).find((p) => {
            if (p.type) return p.type === 'image';
            const str = (p.fileName || p.url || '').toLowerCase();
            return !str.match(/\.(mp4|mp3|wav|m4a|aac|mov|avi|webm)$/i);
          });

          if (
            firstImageMedia &&
            firstImageMedia.url &&
            !firstImageMedia.url.startsWith('data:image')
          ) {
            try {
              const fetchPromise = fetch(firstImageMedia.url).then((res) => res.blob());
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('timeout')), 8000),
              );
              const blob = await Promise.race([fetchPromise, timeoutPromise]);

              const reader = new FileReader();
              const base64 = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              firstImageMedia.url = base64;
            } catch (e) {
              console.warn('Pre-fetch failed for', firstImageMedia.url);
            }
          } else {
            await new Promise((r) => setTimeout(r, delayMs));
          }
        }

        // --- PREPARE HEADER DATA ---
        let regionText = 'All';
        if (currentItem.regionIds && currentItem.regionIds.length > 0) {
          const names = activeRegions
            .filter((r) => currentItem.regionIds.includes(r.id))
            .map((r) => r.name);
          names.sort((a, b) => a.localeCompare(b));
          regionText = names.join(', ');
        } else if (currentItem.regionId && currentItem.regionId !== 'ALL') {
          const region = state.regions.find((r) => r.id === currentItem.regionId);
          if (region) regionText = region.name;
        }

        let categoryText = 'All';
        if (currentItem.categoryIds && currentItem.categoryIds.length > 0) {
          const names = activeCategories
            .filter((c) => currentItem.categoryIds.includes(c.id))
            .map((c) => c.name);
          names.sort((a, b) => a.localeCompare(b));
          categoryText = names.join(', ');
        } else if (currentItem.categoryId && currentItem.categoryId !== 'ALL') {
          const cat = state.categories.find((c) => c.id === currentItem.categoryId);
          if (cat) categoryText = cat.name;
        }

        let dateText = 'All';
        if (currentItem.dateRangeType === 'RANGE') {
          const d1 = formatDate(currentItem.dateFrom);
          const d2 = formatDate(currentItem.dateTo);
          dateText = `${d1} to ${d2}`;
        }

        let effectivelyMultipleRegions = false;
        if (currentItem.regionIds && currentItem.regionIds.length > 1) {
          effectivelyMultipleRegions = true;
        } else if (
          (!currentItem.regionIds || currentItem.regionIds.length === 0) &&
          (!currentItem.regionId || currentItem.regionId === 'ALL')
        ) {
          effectivelyMultipleRegions = true;
        }

        const reportData = {
          title: currentItem.name,
          salutation: currentItem.salutation || '',
          emailClose: currentItem.emailClose || '',
          regionText: regionText,
          categoryText: categoryText,
          dateText: dateText,
          batchText: batchCommaText,
          keyword: currentItem.keywords || '',
          infoFilterA: currentItem.infoFilterA || '',
          infoFilterB: currentItem.infoFilterB || '',
          logo: logoUrl,
          photoDimension: parseInt(currentItem.photoDimension) || 50,
          items: finalMarkers,
          includeHeader: currentItem.includeHeader ?? true,
          allowRowBreaks: allowRowBreaks,
          useRoundedCorners: useRoundedCorners,
          roundedCornerRadius: roundedCornerRadius,
          emailFrom: emailFromAddress,
          isMultipleRegions: effectivelyMultipleRegions,
          defaultFontType: defaultFontType,
          defaultFontSize: defaultFontSize,
          hilightColor: currentItem.hilightColor || '#F5F5F5',
          hilightOpacity:
            currentItem.hilightOpacity !== undefined ? currentItem.hilightOpacity : 100,
        };

        // 5. Generate Documents
        if (wantsWord) {
          setDownloadAlertMessage(
            <Box sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: 1.5 }}>
              {`Processing ${currentItem.name}:\n${batchText ? batchText + '\n\n' : ''}Generating Word Document...`}
            </Box>,
          );
          await forcePaint();
          await generatePortfolioDocx(reportData);
        }
        if (wantsPdf) {
          setDownloadAlertMessage(
            <Box sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: 1.5 }}>
              {`Processing ${currentItem.name}:\n${batchText ? batchText + '\n\n' : ''}Generating PDF Document...`}
            </Box>,
          );
          await forcePaint();
          await generatePortfolioPDF(reportData);
        }
      }

      setShowDownloadAlert(false);
      await new Promise((r) => setTimeout(r, 50));
    } catch (error) {
      console.error('Generation failed:', error);
      setErrorMessage('An error occurred while generating the document(s).');
      setShowErrorAlert(true);
    } finally {
      setGeneratingRowIds([]);
      setIsGenerating(false);
      setShowDownloadAlert(false);
    }
  };

  const disableAddNew = viewMode !== 'LIST';
  const disableDelete = !flags.canDelete || viewMode !== 'LIST';

  const disableDownload = state.selectedIds.length === 0 || isGenerating || flags.isDirty;

  const disableNav = state.selectedIds.length !== 1;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
      {isGenerating && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 100002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      )}

      {/* --- TOOLBAR --- */}
      <Box
        sx={{
          height: '60px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
        }}>
        {/* ADD */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleAddNew}
            disabled={disableAddNew}>
            {disableAddNew ? (
              <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* DOWNLOAD */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleProceed}
            disabled={disableDownload}>
            <MenuDownload
              sx={{
                fontSize: ICON_SIZE,
                opacity: disableDownload ? 0.35 : 1,
                filter: disableDownload ? 'grayscale(100%)' : 'none',
              }}
              iconScale={5}
              iconX={32}
              iconY={38}
              textY={108}
            />
          </IconButton>
        </Box>

        {/* SAVE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleSave}
            disabled={!flags.canSave}>
            {flags.canSave ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* DELETE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleDeleteClick}
            disabled={disableDelete}>
            {disableDelete ? (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* CANCEL */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleLocalCancel}>
            <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
      </Box>

      {/* --- MAIN CONTENT AREA --- */}
      <Box
        sx={{
          flexGrow: 1,
          p: 0,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {viewMode === 'LIST' && (
          <PortfolioList
            loading={state.loading}
            displayRows={state.displayRows}
            selectedIds={state.selectedIds}
            focusedRowId={state.focusedRowId}
            actions={actions}
            mode='PORTFOLIO'
            outputFormats={outputFormats}
            setOutputFormats={setOutputFormats}
            generatingRowIds={generatingRowIds}
          />
        )}

        {viewMode === 'REGIONS' && (
          <PortfolioRegions
            selectedItem={selectedItem}
            activeRegions={activeRegions}
            onToggle={handleRegionToggle}
            onToggleAll={handleToggleAllRegions}
          />
        )}

        {viewMode === 'CATEGORIES' && (
          <PortfolioCategories
            selectedItem={selectedItem}
            activeCategories={activeCategories}
            onToggle={handleCategoryToggle}
            onToggleAll={handleToggleAllCategories}
          />
        )}

        {viewMode === 'SETTINGS' && (
          <PortfolioSettings
            selectedItem={selectedItem}
            actions={actions}
            allBatches={state.allBatches}
          />
        )}
      </Box>

      {/* --- FOOTER --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 3,
          py: 0.5,
          minHeight: '40px',
          borderTop: '1px solid black',
          backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          gap: 1,
          overflow: 'visible',
          position: 'relative',
        }}>
        {/* PAGE INDICATOR DOTS */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            gap: 1,
          }}>
          {activeSteps.map((step) =>
            viewMode === step ? (
              <CircleIcon key={step} sx={{ fontSize: '14px', color: '#0055ff' }} />
            ) : (
              <CircleOutlinedIcon key={step} sx={{ fontSize: '14px', color: '#666' }} />
            ),
          )}
        </Box>

        {/* NAVIGATION ARROWS */}
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleNavFirst}
            disabled={viewMode === activeSteps[0] || disableNav}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <FirstPageIcon
              sx={{
                color: viewMode === activeSteps[0] || disableNav ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavBack}
            disabled={viewMode === activeSteps[0] || disableNav}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowBackIosNewIcon
              sx={{
                color: viewMode === activeSteps[0] || disableNav ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavForward}
            disabled={viewMode === activeSteps[activeSteps.length - 1] || disableNav}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowForwardIosIcon
              sx={{
                color:
                  viewMode === activeSteps[activeSteps.length - 1] || disableNav
                    ? 'grey'
                    : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={handleNavLast}
            disabled={viewMode === activeSteps[activeSteps.length - 1] || disableNav}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <LastPageIcon
              sx={{
                color:
                  viewMode === activeSteps[activeSteps.length - 1] || disableNav
                    ? 'grey'
                    : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* --- DIALOGS --- */}

      {alerts.showUnsaved && (
        <ConfirmDialog
          open={true}
          onClose={() => alerts.setShowUnsaved(false)}
          onConfirm={() => {
            alerts.setShowUnsaved(false);
            actions.resetInterface();
            setViewMode('LIST');
          }}
          message='Cancel without Save will discard your changes?'
          severity='warning'
          disableRestoreFocus={true}
          sx={{ zIndex: 100005 }}
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
        />
      )}

      {alerts.showDelete && (
        <ConfirmDialog
          open={true}
          onClose={() => alerts.setShowDelete(false)}
          onConfirm={actions.handleConfirmDelete}
          message='This action will permanently delete the selected items. Are you sure?'
          severity='error'
          disableRestoreFocus={true}
          sx={{ zIndex: 100005 }}
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
        />
      )}

      {showClearAlert && (
        <ConfirmDialog
          open={true}
          onClose={() => setShowClearAlert(false)}
          onConfirm={handleClearConfirm}
          message='This will clear the detail fields for this item. Are you sure?'
          severity='warning'
          disableRestoreFocus={true}
          sx={{ zIndex: 100005 }}
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
        />
      )}

      {showErrorAlert && (
        <ConfirmDialog
          open={true}
          onClose={() => setShowErrorAlert(false)}
          onConfirm={() => setShowErrorAlert(false)}
          hideCancel={true}
          confirmText='OK'
          message={errorMessage}
          severity='error'
          disableRestoreFocus={true}
          sx={{ zIndex: 100005 }}
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
        />
      )}

      {showDownloadAlert && (
        <ConfirmDialog
          open={true}
          onClose={() => setShowDownloadAlert(false)}
          message={downloadAlertMessage}
          severity={downloadAlertSeverity}
          autoClose={isGenerating ? 0 : 3000}
          disableRestoreFocus={true}
          sx={{ zIndex: 100005 }}
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
        />
      )}
    </Box>
  );
}
